import { apiGet, apiPatch, apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '@/constants/endpoints';

export interface AdminUser {
  _id: string;
  email: string;
  name: string;
  designation?: string;
  city?: string;
  state?: string;
  whatsappNumber?: string;
  isWhatsappVerified: boolean;
  socialMediaLinks: string[];
  profilePictureId?: string | null;
  gender?: string;
  isLookingForJob: boolean;
  jobPreferences: {
    jobTitle?: string;
    jobDescription?: string;
    jobType?: string;
    experienceLevel?: string;
    workMode?: string;
    noticeType?: string;
    currentSalary?: {
      max?: number;
      currency: string;
      denomination?: string;
      period: string;
    };
    expectedSalary?: {
      max?: number;
      currency: string;
      denomination?: string;
      period: string;
    };
    skills: string[];
    location: string[];
    resumeId?: string | null;
  };
  role: string;
  isEmailVerified: boolean;
  loginAttempts: number;
  isActive: boolean;
  membershipTier: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
  aboutMe?: string;
  address?: string;
  dateOfBirth?: string;
  pincode?: string;
}

export interface AdminUsersResponse {
  success: boolean;
  message: string;
  data: {
    users: AdminUser[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}

export interface AdminUserParams {
  page?: number;
  limit?: number;
  search?: string;
  isEmailVerified?: boolean;
  isActive?: boolean;
  role?: string;
  sort?: string;
}

class AdminUserService {
  async getUsers(params?: AdminUserParams): Promise<AdminUsersResponse> {
    try {
      const queryParams: Record<string, string> = {};

      if (params?.page) queryParams.page = params.page.toString();
      if (params?.limit) queryParams.limit = params.limit.toString();
      if (params?.search) queryParams.search = params.search;
      if (params?.isEmailVerified !== undefined) queryParams.isEmailVerified = params.isEmailVerified.toString();
      if (params?.isActive !== undefined) queryParams.isActive = params.isActive.toString();
      if (params?.role) queryParams.role = params.role;
      if (params?.sort) queryParams.sort = params.sort;

      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `${API_ENDPOINTS.ADMIN.USERS}?${queryString}` : API_ENDPOINTS.ADMIN.USERS;

      return await apiGet<AdminUsersResponse>(url);
    } catch (error: any) {
      throw error.response?.data || { message: 'Failed to fetch users' };
    }
  }

  async blockUser(userId: string): Promise<{ success: boolean; message: string }> {
    try {
      return await apiPatch(API_ENDPOINTS.ADMIN.BLOCK_USER(userId));
    } catch (error: any) {
      throw error.response?.data || { message: 'Failed to block user' };
    }
  }

  async unblockUser(userId: string): Promise<{ success: boolean; message: string }> {
    try {
      return await apiPatch(API_ENDPOINTS.ADMIN.UNBLOCK_USER(userId));
    } catch (error: any) {
      throw error.response?.data || { message: 'Failed to unblock user' };
    }
  }

  async exportUsers(params?: AdminUserParams): Promise<Blob> {
    const queryParams: Record<string, string> = {};
    if (params?.search) queryParams.search = params.search;
    if (params?.isEmailVerified !== undefined) queryParams.isEmailVerified = params.isEmailVerified.toString();
    if (params?.isActive !== undefined) queryParams.isActive = params.isActive.toString();
    if (params?.role) queryParams.role = params.role;

    const queryString = new URLSearchParams(queryParams).toString();
    const url = queryString
      ? `${API_ENDPOINTS.ADMIN.USERS_EXPORT}?${queryString}`
      : API_ENDPOINTS.ADMIN.USERS_EXPORT;
    return await apiClient.getBlob(url);
  }
}

export const adminUserService = new AdminUserService();
